import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { MyWorkOrderListItem } from "../api/types";
import {
  formatDate,
  formatWoId,
  woPriorityLabel,
  woStatusLabel,
} from "../lib/workorders";

const MAINTENANCE_CALENDAR_DAYS = 28;

export function MaintenanceCalendarPage() {
  const listQ = useQuery({
    queryKey: ["my-work-orders", "calendar"],
    queryFn: () => api.myWorkOrders.list(),
  });

  const jobs = listQ.data?.workOrders ?? [];
  const calendarStartDate = todayKey();
  const calendarItems = maintenanceCalendarItems(jobs, calendarStartDate);

  return (
    <Layout title="My Calendar">
      <p className="help-line">
        Your assigned work orders by due date. Tap a work order for a quick view or to add it to
        your phone calendar.
      </p>

      <section className="card dashboard-calendar-card maintenance-calendar-card">
        <h2>Work order calendar</h2>
        <p className="sub">Four-week agenda for your assigned work orders by due date.</p>
        {listQ.isLoading ? (
          <p className="panel-empty">Loading...</p>
        ) : listQ.isError ? (
          <p className="panel-empty">Could not load your calendar. Please try again.</p>
        ) : (
          <MaintenanceAgendaCalendar items={calendarItems} startDate={calendarStartDate} />
        )}
      </section>
    </Layout>
  );
}

type MaintenanceCalendarItem = MyWorkOrderListItem & {
  scheduledDate: string;
};

function MaintenanceAgendaCalendar({
  items,
  startDate,
}: {
  items: MaintenanceCalendarItem[];
  startDate: string;
}) {
  const days = calendarDays(startDate, MAINTENANCE_CALENDAR_DAYS);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = items.find((item) => item.id === selectedId);

  return (
    <div className="wo-calendar-demo maintenance-calendar-demo maintenance-calendar-demo--modal">
      <div className="day-agenda-calendar maintenance-agenda-calendar">
        {days.map((day) => {
          const dayItems = items.filter((item) => item.scheduledDate === day.key);
          return (
            <div key={day.key} className={"agenda-day" + (dayItems.length === 0 ? " agenda-day--empty" : "")}>
              <div className="agenda-day-head">
                <strong>{day.weekday}</strong>
                <span>{day.label}</span>
              </div>
              {dayItems.length === 0 ? (
                <p className="calendar-empty-slot">No work orders</p>
              ) : (
                <div className="agenda-worker maintenance-agenda-list">
                  {dayItems.map((item) => (
                    <button
                      key={item.id}
                      className={"agenda-work-order" + (item.id === selected?.id ? " selected" : "")}
                      onClick={() => setSelectedId(item.id)}
                      aria-haspopup="dialog"
                    >
                      <span className="agenda-work-order-id">{formatWoId(item.id)}</span>
                      <span>{item.title}</span>
                      <span className="agenda-work-order-location">{calendarLocation(item)}</span>
                      <span className={`badge badge-${statusBadge(item.status)}`}>
                        {woStatusLabel(item.status)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <MaintenanceCalendarQuickView item={selected} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function MaintenanceCalendarQuickView({
  item,
  onClose,
}: {
  item: MaintenanceCalendarItem | undefined;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!item) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [item, onClose]);

  if (!item) return null;
  return (
    <div className="maintenance-modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="wo-quick-view maintenance-calendar-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-calendar-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="maintenance-modal-close" onClick={onClose} aria-label="Close quick view">
          X
        </button>
        <span className="eyebrow">Quick view</span>
        <h3 id="maintenance-calendar-modal-title">
          {formatWoId(item.id)} - {item.title}
        </h3>
        <dl className="detail-list">
          <div>
            <dt>Due</dt>
            <dd>{formatDate(item.dueDate)}</dd>
          </div>
          <div>
            <dt>Assigned</dt>
            <dd>{formatDate(item.assignedDate)}</dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>
              {item.buildingName}
              {hasLocationDetail(item.where) ? ` - ${item.where}` : ""}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`badge badge-${statusBadge(item.status)}`}>
                {woStatusLabel(item.status)}
              </span>
            </dd>
          </div>
          <div>
            <dt>Priority</dt>
            <dd>{woPriorityLabel(item.priority)}</dd>
          </div>
        </dl>
        <div className="maintenance-calendar-actions">
          <Link to={`/maintenance/${item.id}`} className="btn btn-primary btn-xl">
            Open work order
          </Link>
          <a
            className="btn btn-ghost btn-xl"
            href={calendarDownloadHref(item)}
            download={`${formatWoId(item.id)}.ics`}
          >
            Add to phone calendar
          </a>
        </div>
      </section>
    </div>
  );
}

type WeekDay = { key: string; weekday: string; label: string };

function maintenanceCalendarItems(
  jobs: MyWorkOrderListItem[],
  startDate: string,
): MaintenanceCalendarItem[] {
  const start = parseDateKey(startDate);
  const end = new Date(start);
  end.setDate(start.getDate() + MAINTENANCE_CALENDAR_DAYS - 1);
  return jobs
    .map((job) => ({ ...job, scheduledDate: job.dueDate ?? "" }))
    .filter((job) => {
      if (!job.scheduledDate || job.status === "completed" || job.status === "cancelled") {
        return false;
      }
      const date = parseDateKey(job.scheduledDate);
      return date >= start && date <= end;
    })
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate) || a.id - b.id);
}

function calendarDays(startDate: string, dayCount: number): WeekDay[] {
  const start = parseDateKey(startDate);
  return Array.from({ length: dayCount }).map((_, index) => {
    const d = new Date(start);
    d.setDate(start.getDate() + index);
    return {
      key: dateKey(d),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
  });
}

function calendarDownloadHref(item: MaintenanceCalendarItem): string {
  const title = `${formatWoId(item.id)} - ${item.title}`;
  const location = `${item.buildingName}${hasLocationDetail(item.where) ? ` - ${item.where}` : ""}`;
  const description = [
    item.description || "No description provided.",
    `Status: ${woStatusLabel(item.status)}`,
    `Priority: ${woPriorityLabel(item.priority)}`,
  ].join("\\n");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Royal Heights//Maintenance Work Orders//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:royal-heights-work-order-${item.id}@royal-heights`,
    `DTSTAMP:${icsTimestamp(new Date())}`,
    `DTSTART;VALUE=DATE:${icsDate(item.scheduledDate)}`,
    `DTEND;VALUE=DATE:${icsDate(nextDateKey(item.scheduledDate))}`,
    `SUMMARY:${icsEscape(title)}`,
    `LOCATION:${icsEscape(location)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join("\r\n"))}`;
}

function statusBadge(status: string): string {
  if (status === "completed") return "ok";
  if (status === "on_hold") return "warn";
  if (status === "cancelled") return "muted";
  if (status === "in_progress") return "info";
  return "info";
}

function hasLocationDetail(where: string): boolean {
  const normalized = where.trim();
  return normalized !== "" && !/^[-\u2013\u2014]+$/.test(normalized) && normalized.toLowerCase() !== "n/a";
}

function calendarLocation(item: MaintenanceCalendarItem): string {
  return `${item.buildingName}${hasLocationDetail(item.where) ? ` - ${item.where}` : ""}`;
}

function todayKey(): string {
  return dateKey(new Date());
}

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function parseDateKey(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

function nextDateKey(value: string): string {
  const d = parseDateKey(value);
  d.setDate(d.getDate() + 1);
  return dateKey(d);
}

function icsDate(value: string): string {
  return value.replaceAll("-", "");
}

function icsTimestamp(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function icsEscape(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}
